// ============================================================================
// File Parsing Utilities - CSV & JSON Import
// Handles parsing CSV and JSON files into normalized row data
// ============================================================================

export interface ParsedData {
  headers: string[]
  rows: Record<string, string>[]
  totalRows: number
  errors: ParseError[]
}

export interface ParseError {
  row: number
  message: string
}

/**
 * Parse a CSV string into structured data
 * Handles quoted fields, escaped quotes, mixed line endings
 */
export function parseCSV(csvText: string): ParsedData {
  const errors: ParseError[] = []
  const lines = splitCSVLines(csvText)

  if (lines.length === 0) {
    return { headers: [], rows: [], totalRows: 0, errors: [{ row: 0, message: 'File is empty' }] }
  }

  const headers = parseCSVLine(lines[0]).map(h => h.trim())
  const rows: Record<string, string>[] = []

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue // skip blank lines

    const values = parseCSVLine(line)
    const row: Record<string, string> = {}

    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = j < values.length ? values[j].trim() : ''
    }

    rows.push(row)
  }

  return { headers, rows, totalRows: rows.length, errors }
}

/**
 * Parse a JSON string into structured data
 * Supports array of objects and nested objects (flattened)
 */
export function parseJSON(jsonText: string): ParsedData {
  const errors: ParseError[] = []

  let data: unknown[]
  try {
    const parsed: unknown = JSON.parse(jsonText)
    if (Array.isArray(parsed)) {
      data = parsed
    } else if (typeof parsed === 'object' && parsed !== null) {
      data = [parsed]
    } else {
      return { headers: [], rows: [], totalRows: 0, errors: [{ row: 0, message: 'JSON must be an array of objects' }] }
    }
  } catch (e) {
    return { headers: [], rows: [], totalRows: 0, errors: [{ row: 0, message: `Invalid JSON: ${(e as Error).message}` }] }
  }

  if (data.length === 0) {
    return { headers: [], rows: [], totalRows: 0, errors: [{ row: 0, message: 'JSON array is empty' }] }
  }

  // Flatten and normalize all rows to string values
  const normalizedRows: Record<string, string>[] = []
  const headerSet = new Set<string>()

  for (let i = 0; i < data.length; i++) {
    if (typeof data[i] !== 'object' || data[i] === null) {
      errors.push({ row: i + 1, message: `Row ${i + 1} is not an object, skipping` })
      continue
    }

    const flat = flattenObject(data[i] as Record<string, unknown>)
    const normalized: Record<string, string> = {}

    for (const [key, value] of Object.entries(flat)) {
      headerSet.add(key)
      normalized[key] = value == null ? '' : String(value)
    }

    normalizedRows.push(normalized)
  }

  const headers = Array.from(headerSet)
  const rows = normalizedRows.map(row => {
    const full: Record<string, string> = {}
    for (const h of headers) {
      full[h] = row[h] ?? ''
    }
    return full
  })

  return { headers, rows, totalRows: rows.length, errors }
}

/**
 * Parse a file by detecting its type from extension or content
 */
export async function parseFile(file: File): Promise<ParsedData> {
  const text = await file.text()
  const name = file.name.toLowerCase()

  if (name.endsWith('.json')) {
    return parseJSON(text)
  }

  // Default to CSV for .csv and unknown extensions
  return parseCSV(text)
}

/**
 * Flatten a nested object to dot-notation keys
 * e.g. { a: { b: 1 } } -> { "a.b": "1" }
 */
function flattenObject(obj: Record<string, unknown>, prefix = ''): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenObject(value as Record<string, unknown>, fullKey))
    } else {
      result[fullKey] = Array.isArray(value) ? value.join(', ') : value
    }
  }

  return result
}

/**
 * Split CSV text into lines, respecting quoted fields with embedded newlines
 */
function splitCSVLines(text: string): string[] {
  const lines: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]

    if (ch === '"') {
      if (inQuotes && i + 1 < text.length && text[i + 1] === '"') {
        // Escaped quote
        current += '""'
        i++
      } else {
        inQuotes = !inQuotes
        current += ch
      }
    } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && i + 1 < text.length && text[i + 1] === '\n') {
        i++ // skip \n in \r\n
      }
      lines.push(current)
      current = ''
    } else {
      current += ch
    }
  }

  if (current.trim()) {
    lines.push(current)
  }

  return lines
}

/**
 * Parse a single CSV line into field values
 * Handles quoted fields with embedded commas
 */
function parseCSVLine(line: string): string[] {
  const fields: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const ch = line[i]

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          // Escaped quote
          current += '"'
          i++
        } else {
          // End of quoted field
          inQuotes = false
        }
      } else {
        current += ch
      }
    } else {
      if (ch === '"') {
        inQuotes = true
      } else if (ch === ',') {
        fields.push(current)
        current = ''
      } else {
        current += ch
      }
    }
  }

  fields.push(current)
  return fields
}
